// src/services/mediaCache.ts - FINAL DEFINITIVE FIX
import { isMobileWrapper } from '../utils/runtime';
import { get, set } from 'idb-keyval';

// ---------- Capacitor imports ----------
import { Directory } from '@capacitor/filesystem';
import type { FilesystemPlugin } from '@capacitor/filesystem';

let capacitorFS: FilesystemPlugin | null = null;

async function initCapacitorFS(): Promise<void> {
  if (!capacitorFS) {
    const { Filesystem } = await import('@capacitor/filesystem');
    capacitorFS = Filesystem as FilesystemPlugin;
  }
}

/* -----------------------------------------------------------------
   Helper: Blob ↔ Base64 (needed only for the mobile side)
   ----------------------------------------------------------------- */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function base64ToBlob(b64: string, mime: string): Blob {
  const binary = atob(b64);
  const arr = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    arr[i] = binary.charCodeAt(i);
  }
  return new Blob([arr], { type: mime });
}

/* -----------------------------------------------------------------
   Public API – cacheFile() - ULTIMATE FIX - NO CORS ERRORS
   ----------------------------------------------------------------- */
export async function cacheFile(
  chatId: string,
  messageId: string,
  remoteUrl: string,
  mimeType: string,
): Promise<string> {
  const ext = mimeType.split('/')[1] ?? 'bin';
  const key = `${chatId}_${messageId}.${ext}`;

  // ---------------------------------------------------------------
  // ULTIMATE FIX: Skip caching for ALL Firebase Storage URLs
  // regardless of environment to avoid CORS errors
  // ---------------------------------------------------------------
  const isFirebaseStorage = remoteUrl.includes('firebasestorage.googleapis.com');
  
  if (isFirebaseStorage) {
    return remoteUrl; // Return original URL without any processing
  }

  // ---------------------------------------------------------------
  // Only proceed with caching for non-Firebase URLs
  // ---------------------------------------------------------------
  const isMobile = isMobileWrapper();
  
  // ---------------------------------------------------------------
  // 1️⃣ Try to read from the appropriate cache first
  // ---------------------------------------------------------------
  if (isMobile) {
    // ----- Mobile (Capacitor) -----
    await initCapacitorFS();
    const FS = capacitorFS!;

    try {
      const file = await FS.readFile({
        path: `PCake/${key}`,
        directory: Directory.Documents,
      });
      let blob: Blob;
      if (typeof file.data === 'string') {
        blob = base64ToBlob(file.data, mimeType);
      } else {
        blob = file.data;
      }
      return URL.createObjectURL(blob);
    } catch {
      // Not cached – we'll proceed to download
    }
  } else {
    // ----- Web (IndexedDB) -----
    const cachedBlob = await get<Blob>(key);
    if (cachedBlob) {
      return URL.createObjectURL(cachedBlob);
    }
  }

  // ---------------------------------------------------------------
  // 2️⃣ Not cached → try to fetch from remote URL
  // ---------------------------------------------------------------
  try {
    let response = await fetch(remoteUrl);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    
    if (blob.size === 0) {
      console.warn('Downloaded file is empty, using original URL');
      return remoteUrl;
    }

    // ---------------------------------------------------------------
    // 3️⃣ Store the freshly-downloaded blob
    // ---------------------------------------------------------------
    if (isMobile) {
      const FS = capacitorFS!;
      const base64 = await blobToBase64(blob);
      await FS.writeFile({
        path: `PCake/${key}`,
        data: base64,
        directory: Directory.Documents,
        recursive: true,
      });
      return URL.createObjectURL(blob);
    } else {
      await set(key, blob);
      return URL.createObjectURL(blob);
    }
    
  } catch (error) {
    console.warn('Cache fetch failed, using original URL:', error);
    return remoteUrl;
  }
}

/* -----------------------------------------------------------------
   Optional: prune old entries
   ----------------------------------------------------------------- */
export async function pruneCache(maxEntries = 200): Promise<void> {
  if (isMobileWrapper()) return;

  const allKeys = await (await import('idb-keyval')).keys();
  const pcakeKeys = allKeys.filter(
    (k) => typeof k === 'string' && k.startsWith('pcake_')
  );

  if (pcakeKeys.length <= maxEntries) return;

  const excess = pcakeKeys.length - maxEntries;
  const keysToDelete = pcakeKeys.slice(0, excess);
  for (const k of keysToDelete) {
    await (await import('idb-keyval')).del(k);
  }
}

async function pruneMobile(maxFiles = 200) {
  const { Filesystem, Directory } = await import('@capacitor/filesystem');
  const list = await Filesystem.readdir({
    path: 'PCake',
    directory: Directory.Documents,
  });

  if (list.files.length <= maxFiles) return;

  const excess = list.files.length - maxFiles;
  const filesToDelete = list.files.slice(0, excess);

  for (const f of filesToDelete) {
    await Filesystem.deleteFile({
      path: `PCake/${f}`,
      directory: Directory.Documents,
    });
  }
}